import React, { ComponentProps, createContext, useContext } from "react";
import {
  Modal as RNModal,
  Pressable,
  ScrollView,
  View,
  TouchableOpacity,
  ViewProps,
} from "react-native";
import { X } from "lucide-react-native";
import { Text } from "../Text/Text";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { twMerge } from "tailwind-merge";

type ModalProps = Omit<
  ComponentProps<typeof RNModal>,
  "visible" | "onRequestClose"
> & {
  open: boolean;
  size?: number;
  onClose: () => void;
  children: React.ReactNode;
  stickyFooter?: React.ReactNode;
  backgroundStyle?: ViewProps["style"];
};

const ModalContext = createContext(
  {} as {
    open: boolean;
    onClose: () => void;
  },
);
const useModalContext = () => useContext(ModalContext);

export function Modal({
  size,
  open,
  onClose,
  children,
  stickyFooter,
  backgroundStyle,
  ...props
}: ModalProps) {
  return (
    <ModalContext.Provider value={{ open, onClose }}>
      <RNModal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={onClose}
        {...props}
      >
        <Pressable
          onPress={onClose}
          className="flex-1 items-center justify-center bg-black/50"
        >
          <Pressable
            onPress={(e) => e.stopPropagation()} // Prevent close when clicked inside
            className="max-h-[90%] min-w-[200px] max-w-[90%] cursor-auto rounded-2xl border border-white/10"
            style={[
              backgroundStyle || { backgroundColor: "#0B1121" }, // Default dark background
              size ? { width: size } : undefined,
            ]}
          >
            <View className="flex-1 overflow-hidden rounded-2xl">
              <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
                {children}
              </ScrollView>
              {stickyFooter}
            </View>
          </Pressable>
        </Pressable>
      </RNModal>
    </ModalContext.Provider>
  );
}

type ModalHeaderProps = ViewProps & {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  disableClose?: boolean;
};

export function ModalHeader({
  title,
  subtitle,
  className,
  disableClose = false,
  ...props
}: ModalHeaderProps) {
  const { onClose } = useModalContext();
  const capturePress = usePressCapture("ModalHeader");

  return (
    <View
      className={twMerge(
        "flex-row items-start justify-between border-b border-white/10 p-4",
        className,
      )}
      {...props}
    >
      <View className="flex-1 gap-1">
        {typeof title === "string" ? (
          <Text variant="title" className="font-serif font-bold text-white">
            {title}
          </Text>
        ) : (
          title
        )}
        {typeof subtitle === "string" ? (
          <Text size="sm" className="text-slate-400">
            {subtitle}
          </Text>
        ) : (
          subtitle
        )}
      </View>
      {!disableClose && (
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={() => {
            // Icon-only affordance; its accessibility label is the copy.
            capturePress(pressLabel("Close"), "close_modal");
            onClose();
          }}
          className="p-1"
        >
          <X size={16} className="text-slate-400" />
        </TouchableOpacity>
      )}
    </View>
  );
}

export function ModalContent({ className, ...props }: ViewProps) {
  return <View className={twMerge("flex-1 gap-4 p-4", className)} {...props} />;
}

export function ModalFooter({ className, ...props }: ViewProps) {
  return (
    <View
      className={twMerge(
        "flex-row justify-end gap-2 border-t border-white/10 p-4",
        className,
      )}
      {...props}
    />
  );
}
